import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import * as authApi from "@/lib/api/auth";
import type { User } from "@/lib/api/types";
import {
  getSession,
  startCrossTabSync,
  subscribe,
  updateUser,
  type StoredSession,
} from "@/lib/session/tokens";

export type SessionApi = {
  user: User | null;

  loading: boolean;
  signedIn: boolean;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; displayName: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: User) => void;
};

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<StoredSession | null>(getSession);

  const [loading, setLoading] = useState(() => getSession() !== null);

  useEffect(() => {
    const stopSync = startCrossTabSync();
    const stopListening = subscribe(setSessionState);
    return () => {
      stopSync();
      stopListening();
    };
  }, []);

  useEffect(() => {
    if (getSession() === null) return undefined;
    let cancelled = false;
    authApi
      .me()
      .then((user) => {
        if (!cancelled) updateUser(user);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await authApi.login({ email, password });
  }, []);

  const register = useCallback(
    async (input: { email: string; password: string; displayName: string }) => {
      await authApi.register(input);
    },
    [],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
  }, []);

  const refreshUser = useCallback(async () => {
    updateUser(await authApi.me());
  }, []);

  const api = useMemo<SessionApi>(
    () => ({
      user: session?.user ?? null,
      loading,
      signedIn: session !== null,
      accessToken: session?.accessToken ?? null,
      login,
      register,
      logout,
      refreshUser,
      setUser: updateUser,
    }),
    [session, loading, login, register, logout, refreshUser],
  );

  return <SessionContext value={api}>{children}</SessionContext>;
}

export function useSession(): SessionApi {
  const api = use(SessionContext);
  if (api === null) throw new Error("useSession used outside SessionProvider");
  return api;
}
